import type { CSSProperties, ReactNode } from 'react';
import { tokens } from '@/design';
import type { GTwinRecord } from '@/sdk-stub/gtwin-sdk/types';
import {
  anchorDraftFromRecord,
  formatEndpointList,
  formatEnvelopeSummary,
  itemDraftFromRecord,
  type AnchorInspectorDraft,
  type ItemInspectorDraft,
  type StudioSceneItemEntry,
} from './sceneStructure';

export interface StructureInspectorProps {
  record: GTwinRecord;
  selected: StudioSceneItemEntry | null;
  itemDraft: ItemInspectorDraft | null;
  anchorDraft: AnchorInspectorDraft;
  onItemDraftChange: (draft: ItemInspectorDraft) => void;
  onAnchorDraftChange: (draft: AnchorInspectorDraft) => void;
  onExportGlayer?: () => void;
  exportBusy?: boolean;
  exportError?: string | null;
}

export function StructureInspector({
  record,
  selected,
  itemDraft,
  anchorDraft,
  onItemDraftChange,
  onAnchorDraftChange,
  onExportGlayer,
  exportBusy = false,
  exportError,
}: StructureInspectorProps) {
  const envelope = formatEnvelopeSummary(record);

  return (
    <div
      data-testid="structure-inspector"
      style={{
        overflow: 'auto',
        flex: 1,
        padding: tokens.space[3],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <section aria-labelledby="envelope-heading">
        <h2
          id="envelope-heading"
          style={{
            margin: `0 0 ${tokens.space[2]}px`,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          Envelope
        </h2>
        <dl style={dlStyle}>
          <Dt>Label</Dt>
          <Dd>{envelope.label}</Dd>
          <Dt>Version</Dt>
          <Dd>{envelope.version}</Dd>
          <Dt>Domain</Dt>
          <Dd>{envelope.domain || '—'}</Dd>
          <Dt>Anchor kind</Dt>
          <Dd>{envelope.anchorKind}</Dd>
        </dl>
      </section>

      <section aria-labelledby="anchor-heading" style={{ marginTop: tokens.space[4] }}>
        <h2
          id="anchor-heading"
          style={{
            margin: `0 0 ${tokens.space[2]}px`,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          Project anchor
        </h2>
        {anchorDraft.kind === 'projected' ? (
          <>
            <label style={labelStyle}>
              Horizontal CRS
              <input
                data-testid="anchor-horizontal-crs"
                value={anchorDraft.horizontalCrs}
                onChange={(e) =>
                  onAnchorDraftChange({ ...anchorDraft, horizontalCrs: e.target.value })
                }
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Display CRS
              <input
                data-testid="anchor-display-crs"
                value={anchorDraft.displayCrs}
                onChange={(e) =>
                  onAnchorDraftChange({ ...anchorDraft, displayCrs: e.target.value })
                }
                style={inputStyle}
              />
            </label>
          </>
        ) : (
          <p style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
            Anchor kind is {anchorDraft.kind}. CRS editing applies to projected anchors only.
          </p>
        )}
      </section>

      {selected && itemDraft ? (
        <section aria-labelledby="item-heading" style={{ marginTop: tokens.space[4] }}>
          <h2
            id="item-heading"
            style={{
              margin: `0 0 ${tokens.space[2]}px`,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.semibold,
            }}
          >
            Scene item — {String(selected.item.id)}
          </h2>
          <dl style={dlStyle}>
            <Dt>Slot</Dt>
            <Dd>{selected.slot}</Dd>
            <Dt>Layer kind</Dt>
              <Dd>{String(selected.item.layerKind ?? '—')}</Dd>
            <Dt>Source CRS</Dt>
            <Dd>{String(selected.item.sourceCrs ?? selected.item.crs ?? '—')}</Dd>
            <Dt>Display CRS</Dt>
            <Dd>{String(selected.item.displayCrs ?? '—')}</Dd>
            <Dt>Bbox</Dt>
            <Dd>
              {Array.isArray(selected.item.bbox)
                ? selected.item.bbox.join(', ')
                : '—'}
            </Dd>
          </dl>
          <label style={labelStyle}>
            Title
            <input
              data-testid="item-title"
              value={itemDraft.title}
              onChange={(e) =>
                onItemDraftChange({ ...itemDraft, title: e.target.value })
              }
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            <input
              type="checkbox"
              data-testid="item-visible"
              checked={itemDraft.visible}
              onChange={(e) =>
                onItemDraftChange({ ...itemDraft, visible: e.target.checked })
              }
            />{' '}
            Visible
          </label>
          <label style={labelStyle}>
            Opacity ({itemDraft.opacity.toFixed(2)})
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              data-testid="item-opacity"
              value={itemDraft.opacity}
              onChange={(e) =>
                onItemDraftChange({
                  ...itemDraft,
                  opacity: Number.parseFloat(e.target.value),
                })
              }
              style={{ width: '100%' }}
            />
          </label>
          <label style={labelStyle}>
            Endpoints
            <pre style={preStyle}>{formatEndpointList(selected.item.endpoints)}</pre>
          </label>
          <label style={labelStyle}>
            Style (read-only)
            <pre style={preStyle}>
              {JSON.stringify(selected.item.style ?? {}, null, 2)}
            </pre>
          </label>
          {onExportGlayer ? (
            <div style={{ marginTop: tokens.space[3] }}>
              <button
                type="button"
                data-testid="export-glayer-btn"
                disabled={exportBusy}
                onClick={onExportGlayer}
                style={{
                  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                  borderRadius: tokens.radius.sm,
                  border: tokens.border.thin,
                  background: tokens.color.surface[2],
                  cursor: exportBusy ? 'not-allowed' : 'pointer',
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.size.sm,
                }}
              >
                {exportBusy ? 'Exporting…' : 'Export .glayer'}
              </button>
              {exportError ? (
                <p
                  role="alert"
                  style={{
                    marginTop: tokens.space[2],
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.semantic.danger,
                  }}
                >
                  {exportError}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : (
        <p
          style={{
            marginTop: tokens.space[4],
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          Select a scene item in the tree to inspect and edit mutable fields.
        </p>
      )}
    </div>
  );
}

function Dt({ children }: { children: ReactNode }) {
  return (
    <dt
      style={{
        color: tokens.color.ink.secondary,
        fontSize: tokens.font.size.xs,
        marginTop: tokens.space[1],
      }}
    >
      {children}
    </dt>
  );
}

function Dd({ children }: { children: ReactNode }) {
  return (
    <dd
      style={{
        margin: 0,
        fontSize: tokens.font.size.sm,
        color: tokens.color.ink.primary,
      }}
    >
      {children}
    </dd>
  );
}

const dlLabelColumn = `${tokens.layout.settingsRail / 16 / 2}rem`;

const dlStyle: CSSProperties = {
  margin: 0,
  display: 'grid',
  gridTemplateColumns: `${dlLabelColumn} 1fr`,
  gap: `${tokens.space[1]}px ${tokens.space[2]}px`,
};

const labelStyle: CSSProperties = {
  display: 'block',
  marginTop: tokens.space[3],
  fontSize: tokens.font.size.sm,
  color: tokens.color.ink.secondary,
};

const inputStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: tokens.space[1],
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  border: tokens.border.thin,
  borderRadius: tokens.radius.sm,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  boxSizing: 'border-box',
};

const preStyle: CSSProperties = {
  marginTop: tokens.space[1],
  padding: tokens.space[2],
  background: tokens.color.surface[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.sm,
  fontSize: tokens.font.size.xs,
  overflow: 'auto',
  maxHeight: 160,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

/** Baseline helpers exported for tests */
export function baselineItemDraft(
  selected: StudioSceneItemEntry | null,
): ItemInspectorDraft | null {
  return selected ? itemDraftFromRecord(selected.item) : null;
}

export function baselineAnchorDraft(record: GTwinRecord): AnchorInspectorDraft {
  return anchorDraftFromRecord(record);
}
